<!--
  OrgInstructors — 기관 관리 › 강사관리 (기관 Master 전용)
  · 기관 강사 목록(Master 먼저) → 강사별 클래스 목록 → 기존 클래스 상세(조회 전용)
  · 등록은 강사 동의 코드 절차(2026-09-23): 이메일 검색 → 강사 메일로 6자리 코드 발송 → Master 가 코드 입력 → 소속 생성
  · 해제 시 진행 중 지정이 있으면 확인 후 close_vouchers=true 재요청
  정본: docs/바우처_기관발행_강사화면.md §3.2·§4.1
-->
<template>
  <div class="p-page">
    <a-page-head title="강사관리" :breadcrumbs="[{ label: '기관 관리' }, { label: '강사관리' }]">
      <template #actions>
        <a-btn kind="ghost" size="md" @click="load">새로고침</a-btn>
        <a-btn kind="primary" size="md" :disabled="!orgIdx" @click="showAdd = true">
          <template #icon>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14" /></svg>
          </template>
          강사 등록
        </a-btn>
      </template>
    </a-page-head>

    <org-selector />

    <div class="p-grid p-grid--kpi">
      <a-card padding="20"><a-stat label="기관 강사" :value="String(instructors.length)" unit="명" /></a-card>
      <a-card padding="20"><a-stat label="Master" :value="String(masterCount)" unit="명" /></a-card>
      <a-card padding="20"><a-stat label="진행 중 클래스" :value="String(ongoingClassTotal)" unit="개" /></a-card>
      <a-card padding="20"><a-stat label="수강생" :value="String(studentTotal)" unit="명" /></a-card>
    </div>

    <a-card title="강사 목록" :sub="`총 ${instructors.length}명 · Master 가 먼저 표시됩니다`">
      <div v-if="loading" class="p-loader"><div class="p-spinner" /></div>
      <a-empty v-else-if="!instructors.length" title="등록된 강사가 없습니다" sub="‘강사 등록’ 으로 승인 강사에게 동의 코드를 보내 등록하세요." />
      <a-table v-else :columns="cols" :rows="instructors" row-key="users_idx">
        <template #cell-name="{ row }">
          <div class="p-cell-class is-link" @click="goClassrooms(row)">
            <div class="p-avatar">{{ (row.name || '?').charAt(0) }}</div>
            <div>
              <div class="p-cell-title">{{ row.name || '-' }}</div>
              <div class="p-cell-sub p-mono">{{ row.email }}</div>
            </div>
          </div>
        </template>
        <template #cell-role="{ row }">
          <a-pill :kind="row.role === 'master' ? 'yellow' : 'default'">{{ row.role === 'master' ? 'Master' : '강사' }}</a-pill>
        </template>
        <template #cell-ongoing_class_count="{ value }"><span class="p-mono">{{ value == null ? 0 : value }}</span></template>
        <template #cell-student_count="{ value }"><span class="p-mono">{{ value == null ? 0 : value }}</span></template>
        <template #cell-created_at="{ value }"><span class="p-mono">{{ fmtDateTime(value) }}</span></template>
        <template #cell-actions="{ row }">
          <div class="p-row-actions">
            <a-btn size="sm" kind="ghost" @click="goClassrooms(row)">클래스 보기</a-btn>
            <!-- Master 행에는 해제 버튼을 두지 않는다(관리자만 해제) -->
            <a-btn
              v-if="row.role !== 'master'"
              size="sm"
              kind="danger"
              :disabled="removing === row.users_idx"
              @click="remove(row)"
            >해제</a-btn>
          </div>
        </template>
      </a-table>
    </a-card>

    <!-- 등록 대기(동의 코드 요청) — 강사가 전달한 코드를 여기서도 입력할 수 있다 -->
    <instructor-invites-card ref="invites" :org-idx="orgIdx" class="p-mt-16" @confirmed="onAdded" />

    <instructor-add-modal v-if="showAdd" :org-idx="orgIdx" @close="onModalClose" @added="onAdded" @invited="onInvited" />

    <!-- 해제 — 진행 중 지정 확인 (409 ORG_MEMBER_HAS_ACTIVE_VOUCHERS) -->
    <a-modal
      :open="!!activeVouchersOf"
      title="진행 중인 도서 지정이 있는 강사입니다"
      sub="해제하면 아래 지정이 모두 종료됩니다. 이미 등록한 학생의 도서는 유지됩니다."
      size="md"
      @close="activeVouchersOf = null"
    >
      <ul v-if="activeVouchersOf" class="p-av-list">
        <li v-for="(v, i) in activeVouchersOf.vouchers" :key="i" class="p-av-item">
          <div class="p-cell-title">{{ v.classroom_name || (v.classroom_idx ? `클래스 #${v.classroom_idx}` : '클래스') }}</div>
          <div class="p-cell-sub">
            {{ v.title || v.barcode || '' }}<template v-if="v.grade"> · {{ gradeLabel(v.grade) }}</template><template v-if="v.registered_count != null"> · {{ v.registered_count }}명 등록</template><template v-if="v.register_until"> · 기한 {{ fmtDate(v.register_until) }}</template>
          </div>
        </li>
        <li v-if="!activeVouchersOf.vouchers.length" class="p-cell-sub">지정 목록을 받지 못했습니다. 그래도 해제하면 진행 중 지정이 모두 종료됩니다.</li>
      </ul>
      <template #foot>
        <a-btn kind="ghost" @click="activeVouchersOf = null">취소</a-btn>
        <a-btn kind="danger" :disabled="removing != null" @click="removeForce">지정 종료 후 해제</a-btn>
      </template>
    </a-modal>
  </div>
</template>

<script>
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import AStat from '@/components/Pineapple/AStat.vue';
import ATable from '@/components/Pineapple/ATable.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import AModal from '@/components/Pineapple/AModal.vue';
import OrgSelector from './components/OrgSelector.vue';
import InstructorAddModal from './components/InstructorAddModal.vue';
import InstructorInvitesCard from './components/InstructorInvitesCard.vue';
import { fmtDateTime, fmtDate, gradeLabel, voucherError, voucherErrorText } from './orgUtils';

export default {
  name: 'OrgInstructors',
  components: { APageHead, ACard, AStat, ATable, APill, ABtn, AEmpty, AModal, OrgSelector, InstructorAddModal, InstructorInvitesCard },
  data() {
    return {
      instructors: [],
      loading: false,
      showAdd: false,
      removing: null,        // 해제 진행 중인 users_idx
      activeVouchersOf: null, // { row, vouchers } — 409 로 받은 진행 중 지정 목록
      cols: [
        { key: 'name',                label: '강사' },
        { key: 'role',                label: '역할',          width: '90px' },
        { key: 'ongoing_class_count', label: '진행 중 클래스', width: '120px', align: 'right' },
        { key: 'student_count',       label: '수강생',        width: '90px',  align: 'right' },
        { key: 'created_at',          label: '등록일',        width: '200px' },
        { key: 'actions',             label: '',              width: '190px', align: 'right' },
      ],
    };
  },
  computed: {
    orgIdx() { return this.$store.getters['organization/currentIdx']; },
    masterCount() { return this.instructors.filter(i => i.role === 'master').length; },
    ongoingClassTotal() { return this.instructors.reduce((s, i) => s + (Number(i.ongoing_class_count) || 0), 0); },
    studentTotal() { return this.instructors.reduce((s, i) => s + (Number(i.student_count) || 0), 0); },
  },
  watch: {
    orgIdx() { this.load(); },
  },
  mounted() {
    this.load();
  },
  methods: {
    fmtDateTime,
    fmtDate,
    gradeLabel,
    async load() {
      if (!this.orgIdx) return;
      this.loading = true;
      try {
        const res = await this.$axios({ method: 'get', url: `/voucher/org/${this.orgIdx}/instructors`, loadingBar: false });
        this.instructors = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.instructors = [];
        this.$notifyVue({ type: 'error', title: voucherErrorText(e, '강사 목록을 불러오지 못했습니다.') });
      } finally {
        this.loading = false;
      }
    },
    goClassrooms(row) {
      this.$router.push({ name: 'OrgInstructorClassrooms', query: { users_idx: row.users_idx } });
    },
    onAdded() {
      this.showAdd = false;
      this.load();
      this.reloadInvites();
    },
    onInvited() {
      this.reloadInvites();
    },
    onModalClose() {
      this.showAdd = false;
      this.reloadInvites();
    },
    reloadInvites() {
      if (this.$refs.invites && this.$refs.invites.load) this.$refs.invites.load();
    },
    async remove(row) {
      const confirmation = await this.$swal({
        title: '강사 해제',
        text: `"${row.name}" 을(를) 기관에서 해제하시겠습니까? 해제해도 강사의 클래스는 남습니다.`,
        icon: 'warning',
        showConfirmButton: true, showCancelButton: true,
        confirmButtonText: '해제', cancelButtonText: '취소',
        confirmButtonColor: '#C44A3A',
      });
      if (!confirmation.isConfirmed) return;
      await this.doRemove(row, false);
    },
    async removeForce() {
      const row = this.activeVouchersOf && this.activeVouchersOf.row;
      if (!row) return;
      await this.doRemove(row, true);
    },
    async doRemove(row, closeVouchers) {
      this.removing = row.users_idx;
      try {
        await this.$axios({
          method: 'delete',
          url: `/voucher/org/${this.orgIdx}/instructors/${row.users_idx}`,
          params: { close_vouchers: closeVouchers },
          loadingBar: true,
        });
        this.activeVouchersOf = null;
        this.$notifyVue({ type: 'success', title: `"${row.name}" 강사를 해제했습니다.` });
        this.load();
      } catch (e) {
        const err = voucherError(e);
        if (err.code === 'ORG_MEMBER_HAS_ACTIVE_VOUCHERS' && !closeVouchers) {
          const list = err.detail && Array.isArray(err.detail.active_vouchers) ? err.detail.active_vouchers : [];
          this.activeVouchersOf = { row, vouchers: list };
        } else {
          this.$notifyVue({ type: 'error', title: voucherErrorText(e, '강사를 해제하지 못했습니다.') });
        }
      } finally {
        this.removing = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './_org.scss';

.p-av-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.p-av-item {
  padding: 10px 12px;
  border: 1px solid $p-line;
  border-radius: 8px;
  background: $p-panel-alt;
}
</style>
